import { apiFetch } from "./api.js";

export const IRACING_RANKING_PAGE_SIZE = 100;
const IRACING_RANKING_DATE_FORMATTER = new Intl.DateTimeFormat("es-CO", {
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  month: "short",
  timeZone: "America/Bogota",
  year: "numeric",
});

export async function loadIracingRanking({
  category = "sports_car",
  page = 1,
  limit = IRACING_RANKING_PAGE_SIZE,
  signal,
} = {}) {
  const params = new URLSearchParams({
    category: String(category),
    page: String(page),
    limit: String(limit),
  });
  const data = await apiFetch(`/api/v1/iracing/licenses?${params.toString()}`, {
    signal,
    timeoutMs: 10000,
  });
  return assertIracingRankingResponse(data);
}

export function createIracingRankingRequestCoordinator({ AbortControllerClass = AbortController } = {}) {
  let activeController = null;
  let revision = 0;

  return {
    async run(loader, request) {
      if (typeof loader !== "function") throw new TypeError("IRACING_RANKING_LOADER_REQUIRED");
      const requestRevision = ++revision;
      activeController?.abort("superseded");
      const controller = new AbortControllerClass();
      activeController = controller;

      try {
        const data = assertIracingRankingResponse(await loader({ ...request, signal: controller.signal }));
        if (requestRevision !== revision || controller.signal.aborted) {
          return { request, status: "stale" };
        }
        return { data, request, status: "success" };
      } catch (error) {
        if (requestRevision !== revision || controller.signal.aborted || error?.name === "AbortError") {
          return { request, status: "cancelled" };
        }
        return { error, request, status: "error" };
      } finally {
        if (requestRevision === revision) activeController = null;
      }
    },
    cancel(reason = "cancelled") {
      revision += 1;
      const hadActiveRequest = Boolean(activeController);
      activeController?.abort(reason);
      activeController = null;
      return hadActiveRequest;
    },
  };
}

export function scheduleIracingRankingInitialLoad(task, schedule = queueMicrotask) {
  let cancelled = false;
  schedule(() => {
    if (!cancelled) task();
  });
  return () => {
    cancelled = true;
  };
}

export function formatIracingRankingDate(value, formatter = IRACING_RANKING_DATE_FORMATTER) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return String(formatter.format(date)).replace(/[\u00a0\u202f]/g, " ");
}

export function assertIracingRankingResponse(data) {
  if (!data || !Array.isArray(data.rows) || !Array.isArray(data.categories) || !data.pagination) {
    const error = new Error("No pudimos leer los datos del ranking.");
    error.code = "IRACING_RANKING_RESPONSE_INVALID";
    throw error;
  }
  return data;
}
